/**
 * PosterA3 — print-ready noticeboard poster for ABGN-UK launch.
 *
 * Rendered at 2480 × 3508 pixels (A3 @ 300dpi, portrait).
 * Captured via Playwright headless browser, saved to /public/launch/.
 */
import React from "react";
import DharmaWheel from "../components/DharmaWheel";
import { BABASAHEB_IMG } from "../components/BabasahebPortrait";

// QR for https://cloud-collective-uk.emergent.host (or eventual custom domain)
// Generated in-line via api.qrserver.com fallback — but we build it ourselves to avoid external deps.
// Simple embed: a placeholder block with the URL underneath. The Playwright capture
// script will overlay a generated QR if needed.
const SITE_URL = "ambedkaritebuddhistsociety.org";

const FEATURES = [
  { title: "Home & Vision", body: "A welcoming entrance to our community, mission and gatherings." },
  { title: "About & Constitution", body: "Who we are, why we exist, and the principles that bind us." },
  { title: "Events & Gatherings", body: "Buddha Purnima, Ambedkar Jayanti and monthly Dhamma circles." },
  { title: "The Dhamma", body: "Teachings, the Eightfold Path, three meditations & a guided sit." },
  { title: "Reformers", body: "Ashoka · Phule · Savitribai · Periyar · Babasaheb · Ramabai · Anna Bhau." },
  { title: "Children's Corner", body: "Stories, family reading booklet and gentle introductions for the young." },
  { title: "Education Series", body: "A 5-stage values-led programme for British schools (ages 4–16)." },
  { title: "Membership & Donations", body: "Join the sangha. Stand with us. Give as a friend or as a family." },
];

export default function PosterA3() {
  return (
    <div
      data-testid="poster-a3"
      style={{
        width: 2480,
        height: 3508,
        background: "#001B3A",
        color: "#FDFBF7",
        fontFamily: "'Inter', system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle radial wash */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 18% 14%, rgba(207,160,82,0.18) 0%, transparent 45%), radial-gradient(circle at 88% 92%, rgba(224,122,95,0.14) 0%, transparent 50%)",
        }}
      />
      {/* Big watermark wheel */}
      <div
        style={{
          position: "absolute",
          right: -340,
          bottom: -340,
          opacity: 0.07,
        }}
      >
        <DharmaWheel size={1600} color="#CFA052" ringColor="#CFA052" />
      </div>

      {/* TOP BAND */}
      <div style={{ padding: "100px 160px 0", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 36 }}>
          <DharmaWheel size={120} color="#CFA052" ringColor="#001B3A" />
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 64, lineHeight: 1, fontWeight: 600 }}>
              Ambedkarite Buddhist Global Network UK
            </div>
            <div style={{ fontSize: 20, letterSpacing: "0.32em", color: "#CFA052", marginTop: 14, textTransform: "uppercase", fontWeight: 600 }}>
              Rooted in Dhamma · Inspired by Babasaheb
            </div>
          </div>
        </div>
        <div style={{ marginTop: 40, height: 2, background: "linear-gradient(90deg, #CFA052 0%, transparent 70%)" }} />
      </div>

      {/* HERO */}
      <div style={{ padding: "120px 160px 80px", position: "relative", display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 100, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: 22, letterSpacing: "0.32em", color: "#E07A5F", textTransform: "uppercase", fontWeight: 600 }}>
            CIO Application in Preparation · Now Live
          </div>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 200,
              lineHeight: 0.95,
              fontWeight: 500,
              margin: "44px 0 0",
              letterSpacing: "-0.01em",
            }}
          >
            A charitable network <span style={{ color: "#CFA052", fontStyle: "italic" }}>for our sangha.</span>
          </h1>
          <p
            style={{
              fontSize: 38,
              lineHeight: 1.45,
              color: "rgba(253,251,247,0.78)",
              margin: "60px 0 0",
              maxWidth: 1100,
            }}
          >
            The <strong style={{ color: "#FDFBF7" }}>Ambedkarite Buddhist Global Network UK</strong> advances
            Buddhist education, meditation, equality and public community participation through in-person
            activities and our own <strong style={{ color: "#FDFBF7" }}>charitable digital platform</strong>.
          </p>
          <div style={{ marginTop: 70, padding: "28px 36px", background: "rgba(207,160,82,0.12)", borderLeft: "6px solid #CFA052", borderRadius: 4 }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 44, color: "#CFA052", lineHeight: 1.3, margin: 0 }}>
              "Educate. Agitate. Organise."
            </p>
            <p style={{ fontSize: 22, color: "rgba(253,251,247,0.7)", marginTop: 12, marginBottom: 0 }}>
              — Babasaheb Dr. B.R. Ambedkar
            </p>
          </div>
        </div>

        {/* Babasaheb portrait card */}
        <div
          style={{
            background: "rgba(253,251,247,0.05)",
            border: "1px solid rgba(207,160,82,0.4)",
            borderRadius: 8,
            padding: 28,
          }}
        >
          <div style={{ aspectRatio: "4/5", overflow: "hidden", borderRadius: 4, background: "#001B3A" }}>
            <img
              src={BABASAHEB_IMG}
              alt="Babasaheb"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 28%" }}
            />
          </div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, color: "#FDFBF7", marginTop: 24, marginBottom: 6 }}>
            Babasaheb
          </p>
          <p style={{ fontSize: 16, letterSpacing: "0.22em", color: "#E07A5F", textTransform: "uppercase", margin: 0 }}>
            Dr. B.R. Ambedkar · 1891–1956
          </p>
        </div>
      </div>

      {/* FEATURES GRID */}
      <div style={{ padding: "0 160px 80px", position: "relative" }}>
        <p style={{ fontSize: 22, letterSpacing: "0.32em", color: "#E07A5F", textTransform: "uppercase", fontWeight: 600, marginBottom: 24 }}>
          What you'll find inside
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 28 }}>
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              style={{
                background: "rgba(253,251,247,0.04)",
                border: "1px solid rgba(207,160,82,0.18)",
                padding: "32px 30px",
                borderRadius: 6,
              }}
            >
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 56, color: "#CFA052", lineHeight: 1, marginBottom: 14 }}>
                0{i + 1}
              </div>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: "#FDFBF7", margin: 0 }}>
                {f.title}
              </p>
              <p style={{ fontSize: 18, lineHeight: 1.45, color: "rgba(253,251,247,0.72)", marginTop: 12, marginBottom: 0 }}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA + URL */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "70px 160px",
          background: "linear-gradient(180deg, transparent 0%, #000B1F 60%)",
          borderTop: "1px solid rgba(207,160,82,0.25)",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 100, alignItems: "center" }}>
          <div>
            <p style={{ fontSize: 20, letterSpacing: "0.32em", color: "#E07A5F", textTransform: "uppercase", fontWeight: 600, marginBottom: 18 }}>
              Visit · Read · Sit · Belong
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 76, color: "#CFA052", margin: 0, lineHeight: 1, letterSpacing: "-0.01em", wordBreak: "break-all" }}>
              {SITE_URL}
            </p>
            <p style={{ fontSize: 22, color: "rgba(253,251,247,0.6)", marginTop: 28, marginBottom: 0 }}>
              info@ambedkaritebuddhistglobalnetworkuk.com · UK-wide charitable community · CIO application in preparation
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 64, fontStyle: "italic", color: "#CFA052", margin: 0, lineHeight: 1.15 }}>
              Educate<br />Meditate<br />Empower<br />Elevate
            </p>
            <p style={{ fontSize: 18, color: "rgba(253,251,247,0.55)", marginTop: 28, fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>
              Rooted in Dhamma · Committed to humanity
            </p>
            <p style={{ fontSize: 20, color: "rgba(253,251,247,0.65)", marginTop: 12, fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>
              Jai Bhim · Namo Buddhāya
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
